import { Link } from "react-router-dom";
import { Phone, Mail, ArrowUpRight, Leaf, HeartHandshake, MessagesSquare, FolderCheck, Users, Heart, Handshake } from "lucide-react";
import Seo from "../components/Seo";
import PageHeader from "../components/PageHeader";
import CtaSection from "../components/CtaSection";
import BookingLink from "../components/BookingLink";
import { Reveal, RevealGroup, RevealItem } from "../components/Reveal";
import { BUSINESS } from "../lib/site";

const VALUES = [
  {
    icon: HeartHandshake,
    title: "We take the time to know you",
    body: "Your money has a purpose. The business you’re building, the family you’re raising, the future you’re picturing. Understanding that comes before any ledger entry.",
    testId: "about-value-know-you",
  },
  {
    icon: MessagesSquare,
    title: "Plain language, always",
    body: "Accounting has a habit of hiding simple ideas behind jargon. We explain everything the way we’d explain it to family. Patiently, and as many times as you need.",
    testId: "about-value-plain-language",
  },
  {
    icon: FolderCheck,
    title: "Organized, then confident",
    body: "Clear numbers come from calm, consistent organization. Once your records are in order, every decision after that gets easier.",
    testId: "about-value-organized",
  },
];

export default function About() {
  return (
    <>
      <Seo
        title="About Our Accounting Team | Directive Optive Solutions"
        description="Meet Directive Optive Solutions: a full cycle accounting firm with over a decade of accounting management experience, serving businesses, individuals, and families."
        path="/about"
      />
      <PageHeader
        eyebrow="About us"
        title="Business bookkeeping and accounting, handled by people who know you."
        lead="Directive Optive Solutions, LLC is a full cycle accounting firm serving business owners, self-employed professionals, individuals, and families. Backed by over a decade of accounting management experience."
        image="/img/photo-us-map.webp"
        imageAlt="A green map of the United States marked with client locations"
      />

      <section className="relative overflow-hidden py-24 lg:py-32" data-testid="about-story-section">
        <img src="/img/photo-meeting.jpg" alt="" aria-hidden width="1200" height="800" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
        <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/90 to-white/60" />
        <div className="container-shell relative z-10">
          <Reveal className="max-w-2xl">
            <p className="eyebrow">Our story</p>
            <h2 className="mt-5 font-serif text-3xl leading-tight text-olive sm:text-4xl">We wanted to build the firm we’d want to walk into.</h2>
            <p className="mt-6 text-base leading-relaxed text-moss sm:text-lg">Directive Optive Solutions was founded on a simple belief: the person keeping your books should be the person who knows your name. And your business, and what you’re working toward.</p>
            <p className="mt-4 text-base leading-relaxed text-moss sm:text-lg">With over a decade of accounting management experience, we’ve seen every kind of ledger. The immaculate, the improvised, and the shoebox. We keep our client list human-sized. We explain before we execute. And we treat “getting organized” not as a scolding but as a relief. The moment your finances stop being a weight and start being a tool.</p>
            <blockquote className="mt-9 border-l-2 border-olive/40 pl-6 font-serif text-2xl italic leading-snug text-pine">{BUSINESS.tagline}</blockquote>
          </Reveal>
        </div>
      </section>

      <section className="relative bg-sage/85 py-24 lg:py-32" data-testid="about-values-section">
        <div className="container-shell relative z-10">
          <Reveal className="max-w-3xl">
            <p className="eyebrow">What we believe</p>
            <h2 className="mt-5 font-serif text-3xl text-olive sm:text-4xl">Three things you can count on.</h2>
          </Reveal>
          <RevealGroup className="mt-14 grid gap-6 md:grid-cols-3">
            {VALUES.map((v) => (
              <RevealItem key={v.title} className="h-full">
                <div className="flex h-full flex-col rounded-md border border-line bg-white p-8" data-testid={v.testId}>
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-sage text-olive"><v.icon className="h-5 w-5" /></span>
                  <h3 className="mt-6 font-serif text-2xl text-olive">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-moss">{v.body}</p>
                </div>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      <section className="relative overflow-hidden py-24 lg:py-32" data-testid="about-family-section">
        <img src="/img/photo-team-support.webp" alt="" aria-hidden width="636" height="279" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
        <div aria-hidden className="absolute inset-0 bg-sage/90" />
        <div className="container-shell relative z-10">
          <Reveal className="max-w-2xl">
            <p className="eyebrow">A different kind of team</p>
            <h2 className="mt-5 text-balance font-serif text-3xl leading-tight text-olive sm:text-4xl">We’re more than colleagues. We’re family.</h2>
            <p className="mt-6 text-base leading-relaxed text-moss sm:text-lg">At Directive Optive Solutions, we treat our clients like family. We take the time to understand your unique needs, offer personalized support, and celebrate your wins — because your success is our success.</p>
            <ul className="mt-8 space-y-4">
              <li className="flex items-start gap-4"><span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-olive"><Users className="h-5 w-5" /></span><div><p className="font-bold text-ink">Personalized support</p><p className="mt-1 text-sm leading-relaxed text-moss">You are never a file number. Support is shaped around your situation.</p></div></li>
              <li className="flex items-start gap-4"><span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-olive"><Heart className="h-5 w-5" /></span><div><p className="font-bold text-ink">Long-term relationships</p><p className="mt-1 text-sm leading-relaxed text-moss">We stay with you through growth, changes, and the years after.</p></div></li>
              <li className="flex items-start gap-4"><span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-olive"><Handshake className="h-5 w-5" /></span><div><p className="font-bold text-ink">Your goals. Our commitment.</p><p className="mt-1 text-sm leading-relaxed text-moss">We measure our work by what it makes possible for you.</p></div></li>
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="py-24 lg:py-32" data-testid="about-scope-section">
        <div className="container-shell grid gap-14 lg:grid-cols-2">
          <Reveal>
            <h2 className="font-serif text-3xl text-olive sm:text-4xl">What we do</h2>
            <p className="mt-6 text-base leading-relaxed text-moss sm:text-lg">Bookkeeping and ongoing records, accounts payable and receivable, bank reconciliations, payroll support, budgeting, 1099 support, account setup, and financial statements and reporting. Plus business and personal tax planning conversations.</p>
            <Link to="/services" data-testid="about-services-link" className="mt-7 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-pine hover:text-olive">
              See all services <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif text-3xl text-olive sm:text-4xl">What we don’t do. Yet</h2>
            <ul className="mt-6 space-y-4 text-base leading-relaxed text-moss">
              <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-olive" />We don’t collect tax documents or sensitive information through this public website.</li>
              <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-olive" />There’s no client login here. A secure portal is something we’d add only when it’s genuinely configured.</li>
              <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-olive" />We don’t make promises about deductions, refunds, or savings before we’ve seen your situation and confirmed scope.</li>
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="relative bg-sage/85 py-24 lg:py-32" data-testid="about-contact-section">
        <div className="container-shell relative z-10 flex flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between">
          <Reveal>
            <p className="eyebrow">Talk to us</p>
            <h2 className="mt-4 font-serif text-3xl text-olive sm:text-4xl">The first conversation is simply a conversation.</h2>
          </Reveal>
          <Reveal delay={0.12} className="flex flex-col gap-3 text-sm font-semibold text-olive sm:flex-row sm:gap-8">
            <a href={BUSINESS.phoneHref} data-testid="about-phone-link" className="inline-flex items-center gap-2 hover:text-pine"><Phone className="h-4 w-4" /> {BUSINESS.phone}</a>
            <a href={BUSINESS.emailHref} data-testid="about-email-link" className="inline-flex items-center gap-2 hover:text-pine"><Mail className="h-4 w-4" /> {BUSINESS.email}</a>
            <BookingLink page="about-page" dataTestId="about-book-button" className="btn-primary !px-6 !py-3">Book a Consultation</BookingLink>
          </Reveal>
        </div>
      </section>

      <CtaSection />
    </>
  );
}
